<template>
  <div class="flex flex-col items-center w-full pb-10 bg-[#F2F6FA]">
    <section
      class="flex flex-col items-center w-full justify-center "
    >
      <!-- Main Dashboard Container -->
      <div class="w-full bg-white rounded-lg shadow-md">
        <!-- Dashboard Header with Controls -->
        <div
          class="flex items-center justify-between w-full px-4 py-3 border-b"
        >
          <div class="flex items-center gap-3">
            <h1 class="text-lg font-bold hidden md:block">Reviews Dashboard</h1>
            <div class="location-dropdown relative">
              <div class="flex items-center gap-2">
                <v-btn
                  @click="toggleStoreDropdown($event)"
                  small
                  outlined
                  color="primary"
                  class="text-sm"
                >
                  {{ selectedWIDs?.length || 0 }} Locations
                  <v-icon small right>mdi-chevron-down</v-icon>
                </v-btn>
                <v-progress-circular
                  v-if="isLoading"
                  indeterminate
                  color="primary"
                  size="16"
                ></v-progress-circular>
              </div>
              <LocationDropdown
                class="custom-dropdown"
                ref="locationDropdown"
                :stores="filteredStores"
                :showStoreDropdown="showStoreDropdown"
                v-model="selectedWIDs"
                @change="handleLocationChange"
              />
            </div>
            <p
              v-if="hasShallowLocations"
              class="text-xs font-medium text-orange-500 ml-2"
            >
              <v-icon small color="orange">mdi-information</v-icon>
              Limited data for partially connected locations
            </p>
          </div>

          <div class="flex items-center gap-3">
            <v-select
              v-model="selectedTimeRange"
              :items="timeRangeItems"
              @change="handleTimeRangeChange"
              label="Time Range"
              dense
              hide-details
              outlined
              class="w-[120px]"
            >
              <template v-slot:selection="{ item }">
                <span class="text-sm">{{ item.text }}</span>
              </template>
            </v-select>
            <v-btn
              color="primary"
              icon
              small
              title="Refresh data"
              @click="refreshData"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn
              color="primary"
              icon
              small
              title="Save dashboard as image"
              @click="captureScreenshot"
            >
              <v-icon>mdi-download-box-outline</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Loading View -->
        <div
          v-if="isLoading"
          class="flex justify-center items-center py-8"
        >
          <div class="p-6 bg-white rounded-lg max-w-2xl mx-auto text-center">
            <div class="flex flex-col items-center justify-center gap-4">
              <v-progress-circular
                indeterminate
                color="primary"
                :size="50"
                :width="5"
              ></v-progress-circular>
              <p class="text-lg font-medium text-gray-700">Loading review dashboard...</p>
              <p class="text-sm text-gray-500">Please wait while we prepare your analytics.</p>
            </div>
          </div>
        </div>
        
        <!-- No Locations View -->
        <div
          v-else-if="!isLoading && selectedWIDs.length === 0"
          class="flex justify-center items-center py-8"
        >
          <div class="p-6 bg-white rounded-lg max-w-2xl mx-auto text-center">
            <NoLocationsConnected
              message="Connect your Google Business locations with Fly to start tracking reviews and ratings."
              actionText="Connect Google Locations"
              @click.native="handleConnectGMB"
            />
          </div>
        </div>

        <!-- Dashboard Content -->
        <div v-else>
          <!-- Key Metrics - Stats Cards Row -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 px-4 pt-3">
            <!-- Rating Card -->
            <div class="bg-white rounded-lg border p-3">
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-1">
                  <h2 class="text-sm font-semibold">Overall Rating</h2>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon size="14" color="grey" v-bind="attrs" v-on="on"
                        >mdi-information-outline</v-icon
                      >
                    </template>
                    <span
                      >Average star rating across all selected locations</span
                    >
                  </v-tooltip>
                </div>
                <span class="text-sm text-gray-600 font-semibold"
                  >Total: {{ totalReviews }}</span
                >
              </div>

              <div class="flex items-center mt-2 justify-between">
                <span class="text-2xl font-bold"
                  >{{ averageRating.toFixed(1) }}
                  <span class="text-yellow-500">★</span></span
                >
                <div class="flex items-center gap-3 text-xs">
                  <div class="flex items-center">
                    <span class="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    <span class="text-green-600">+{{ positiveCount }}</span>
                  </div>
                  <div class="flex items-center">
                    <span class="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                    <span class="text-red-600">-{{ negativeCount }}</span>
                  </div>
                </div>
              </div>

              <!-- Compact Star Bars -->
              <div class="mt-2 grid grid-cols-5 gap-1">
                <div
                  v-for="star in [5, 4, 3, 2, 1]"
                  :key="star"
                  class="flex flex-col"
                >
                  <div class="flex items-center mb-1">
                    <span class="text-xs text-gray-500">{{ star }}★</span>
                  </div>
                  <div class="h-[40px] relative bg-gray-100 rounded-sm w-full">
                    <div
                      class="absolute bottom-0 rounded-sm w-full"
                      :class="{
                        'bg-green-500': star >= 4,
                        'bg-yellow-500': star === 3,
                        'bg-orange-500': star === 2,
                        'bg-red-500': star === 1,
                      }"
                      :style="{ height: getStarPercentage(star) + '%' }"
                    ></div>
                  </div>
                  <span class="text-[10px] text-center mt-1"
                    >{{ getStarPercentage(star) }}%</span
                  >
                </div>
              </div>
            </div>

            <!-- Text vs No Text Card -->
            <div class="bg-white rounded-lg border p-3">
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-1">
                  <h2 class="text-sm font-semibold">Reviews with Text</h2>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon size="14" color="grey" v-bind="attrs" v-on="on"
                        >mdi-information-outline</v-icon
                      >
                    </template>
                    <span>Percentage of reviews with text comments</span>
                  </v-tooltip>
                </div>
              </div>

              <div class="flex items-center justify-between mt-3">
                <div class="flex items-center gap-2">
                  <svg width="70" height="70" viewBox="0 0 36 36">
                    <circle
                      cx="18"
                      cy="18"
                      r="15.9155"
                      fill="transparent"
                      stroke="#E5E7EB"
                      stroke-width="3"
                    ></circle>
                    <circle
                      cx="18"
                      cy="18"
                      r="15.9155"
                      fill="transparent"
                      stroke="#3B82F6"
                      stroke-width="3"
                      :stroke-dasharray="`${textPercentage} ${
                        100 - textPercentage
                      }`"
                      stroke-linecap="round"
                      transform="rotate(-90 18 18)"
                    ></circle>
                    <text
                      x="18"
                      y="20"
                      text-anchor="middle"
                      fill="#3B82F6"
                      font-size="10"
                      font-weight="bold"
                    >
                      {{ textPercentage }}%
                    </text>
                  </svg>
                </div>

                <div class="flex-1 ml-3">
                  <div class="grid grid-cols-2 gap-2 text-xs">
                    <div class="flex items-center">
                      <span
                        class="w-2 h-2 bg-blue-500 rounded-full mr-1"
                      ></span>
                      <span>With Text:</span>
                    </div>
                    <span class="text-right"
                      >{{ textReviews }} ({{ textPercentage }}%)</span
                    >

                    <div class="flex items-center">
                      <span
                        class="w-2 h-2 bg-gray-200 rounded-full mr-1"
                      ></span>
                      <span>No Text:</span>
                    </div>
                    <span class="text-right"
                      >{{ noTextReviews }} ({{ noTextPercentage }}%)</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Replied vs Not Replied Card -->
            <div class="bg-white rounded-lg border p-3">
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-1">
                  <h2 class="text-sm font-semibold">Response Rate</h2>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon size="14" color="grey" v-bind="attrs" v-on="on"
                        >mdi-information-outline</v-icon
                      >
                    </template>
                    <span>Percentage of reviews with owner responses</span>
                  </v-tooltip>
                </div>
              </div>

              <div class="flex items-center justify-between mt-3">
                <div class="flex items-center gap-2">
                  <svg width="70" height="70" viewBox="0 0 36 36">
                    <circle
                      cx="18"
                      cy="18"
                      r="15.9155"
                      fill="transparent"
                      stroke="#E5E7EB"
                      stroke-width="3"
                    ></circle>
                    <circle
                      cx="18"
                      cy="18"
                      r="15.9155"
                      fill="transparent"
                      stroke="#10B981"
                      stroke-width="3"
                      :stroke-dasharray="`${repliedPercentage} ${
                        100 - repliedPercentage
                      }`"
                      stroke-linecap="round"
                      transform="rotate(-90 18 18)"
                    ></circle>
                    <text
                      x="18"
                      y="20"
                      text-anchor="middle"
                      fill="#10B981"
                      font-size="10"
                      font-weight="bold"
                    >
                      {{ repliedPercentage }}%
                    </text>
                  </svg>
                </div>

                <div class="flex-1 ml-3">
                  <div class="grid grid-cols-2 gap-2 text-xs">
                    <div class="flex items-center">
                      <span
                        class="w-2 h-2 bg-green-500 rounded-full mr-1"
                      ></span>
                      <span>Replied:</span>
                    </div>
                    <span class="text-right"
                      >{{ repliedReviews }} ({{ repliedPercentage }}%)</span
                    >

                    <div class="flex items-center">
                      <span
                        class="w-2 h-2 bg-gray-200 rounded-full mr-1"
                      ></span>
                      <span>Not Replied:</span>
                    </div>
                    <span class="text-right"
                      >{{ notRepliedReviews }} ({{
                        notRepliedPercentage
                      }}%)</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Monthly Chart Section - Collapsible -->
          <div class="px-4 pt-4 pb-5">
            <div class="bg-white rounded-lg border p-3">
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-1">
                  <h2 class="text-sm font-semibold">
                    Monthly Reviews & Rating Trends
                  </h2>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon size="14" color="grey" v-bind="attrs" v-on="on"
                        >mdi-information-outline</v-icon
                      >
                    </template>
                    <span>Shows monthly review count and average rating</span>
                  </v-tooltip>
                </div>
              </div>

              <!-- Chart Area -->
              <div style="height: 280px; position: relative" class="mt-3">
                <canvas ref="barChartCanvas"></canvas>
              </div>

              <!-- Compact Scroller -->
              <div class="chart-scroller mt-1">
                <div class="flex items-center justify-between">
                  <button class="scroll-btn-sm" @click="scrollChart('left')">
                    <v-icon small>mdi-chevron-left</v-icon>
                  </button>
                  <div
                    class="relative w-full h-1 bg-gray-200 rounded overflow-hidden mx-1"
                  >
                    <div
                      class="scroll-track w-full h-full"
                      ref="scrollTrack"
                      @mousedown="startDrag"
                      @touchstart="startDrag"
                    >
                      <div
                        class="scroll-thumb bg-primary h-full rounded"
                        :style="{
                          width: thumbWidth + '%',
                          left: thumbPosition + '%',
                        }"
                        ref="scrollThumb"
                      ></div>
                    </div>
                  </div>
                  <button class="scroll-btn-sm" @click="scrollChart('right')">
                    <v-icon small>mdi-chevron-right</v-icon>
                  </button>
                </div>
              </div>

              <!-- Compact Legend -->
              <div
                class="flex flex-wrap justify-center items-center gap-2 mt-2 text-xs"
              >
                <div class="flex items-center">
                  <span class="w-3 h-3 bg-green-600 rounded-full mr-1"></span>
                  <span>5★</span>
                </div>
                <div class="flex items-center">
                  <span class="w-3 h-3 bg-green-400 rounded-full mr-1"></span>
                  <span>4★</span>
                </div>
                <div class="flex items-center">
                  <span class="w-3 h-3 bg-yellow-500 rounded-full mr-1"></span>
                  <span>3★</span>
                </div>
                <div class="flex items-center">
                  <span class="w-3 h-3 bg-orange-500 rounded-full mr-1"></span>
                  <span>2★</span>
                </div>
                <div class="flex items-center">
                  <span class="w-3 h-3 bg-[#fc164c] rounded-full mr-1"></span>
                  <span>1★</span>
                </div>
                <div class="flex items-center">
                  <span class="w-3 h-3 bg-[#f34409] rounded-full mr-1"></span>
                  <span>0★</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  collection,
  onSnapshot,
  query,
  where,
  getDoc,
  doc,
  getDocs,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "@/utils/firebase.utils";
import LocationDropdown from "@/components/LocationDropdown.vue";
import { Chart, registerables } from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import html2canvas from "html2canvas";
import connectedAppsMixins from "@/mixins/connected.apps.mixins";
import NoLocationsConnected from "@/components/ui/common/NoLocationsConnected.vue";
Chart.register(...registerables, zoomPlugin);

export default {
  components: {
    LocationDropdown,
    NoLocationsConnected,
  },
  mixins: [connectedAppsMixins],
  data() {
    return {
      stores: [],
      showStoreDropdown: false,
      selectedWIDs: [],

      // Dashboard data arrays:
      monthlyBuckets: [], // from API
      presentMonthData: {}, // from API, if you want to use it

      // Aggregated UI fields:
      totalReviews: 0,
      averageRating: 0,
      ratingBreakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },

      positiveCount: 0,
      negativeCount: 0,

      // For text vs no text:
      textReviews: 0,
      noTextReviews: 0,
      textPercentage: 0,
      noTextPercentage: 0,

      // For replied vs not replied:
      repliedReviews: 0,
      notRepliedReviews: 0,
      repliedPercentage: 0,
      notRepliedPercentage: 0,

      // Time range filter v-select
      selectedTimeRange: "THREE", // e.g. 'ONE', 'TWO', 'THREE', or null for "All Time"
      timeRangeItems: [
        { text: "All Time", value: null },
        { text: "Last 1 Month", value: "ONE" },
        { text: "Last 6 Months", value: "TWO" },
        { text: "Last 12 Months", value: "THREE" },
      ],

      // For chart scrolling
      thumbWidth: 30, // percentage width of the scroll thumb
      thumbPosition: 0, // percentage position of the scroll thumb
      isDragging: false,
      startX: 0,
      scrollStartPosition: 0,
      visibleMonths: 12, // Number of months to show at once

      // Chart stuff
      chartInstance: null,
      isLoading: false,

      // Cache control variables
      cacheDuration: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
    };
  },
  computed: {
    filteredStores() {
      // Return all stores or filter as you prefer
      return this.stores;
    },
    hasShallowLocations() {
      return this.stores.some(
        (store) => store.shallowConnect && this.selectedWIDs.includes(store.wid)
      );
    },
  },
  mounted() {
    // Load initial store data
    this.fetchStores();

    // Force initial load with Last 12 Months (debug)
    console.log(
      "Component mounted, selectedTimeRange:",
      this.selectedTimeRange
    );

    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("mousemove", this.doDrag);
    document.removeEventListener("mouseup", this.stopDrag);
    document.removeEventListener("touchmove", this.doDrag);
    document.removeEventListener("touchend", this.stopDrag);
  },
  watch: {
    // update locations on change in query
    "$route.query.location": {
      handler(newVal) {
        if (newVal) {
          this.selectedWIDs = newVal.split(",");
          // also check that store in this.stores
          this.stores.forEach((store) => {
            store.checked = this.selectedWIDs.includes(store.wid);
          });
        }
      },
    },
    globalStores: {
      immediate: true,
      handler(newStores) {
        if (newStores && newStores.length > 0) {
          this.fetchStores();
        }
      },
    },
  },
  methods: {
    // 1) Whenever you check/uncheck a location in the dropdown:
    async handleLocationChange(profile) {
      this.isLoading = true;
      // You might store the selected WIDs in your store objects
      // once you confirm them, call fetchReviewsAnalyticsForAllLocations
      try {
        this.selectedWIDs = this.stores
          .filter((item) => item.checked) // Get checked items
          .map((item) => item.id); // Extract their IDs

        // Update the URL with the selected locations
        let query = { ...this.$route.query };
        if (this.selectedWIDs.length > 0) {
          this.modifyUrlParams("location", this.selectedWIDs.join(','));
        } else {
          delete query.location;
          this.$router.replace({ query });
        }
        // If selectedWIDs is empty, push all item IDs
        if (this.selectedWIDs.length === 0) {
          this.selectedWIDs = this.stores.map((item) => item.id);
        }

        this.selectedWID = profile.wid;
        if (this.selectedWIDs.length > 0) {
          await this.fetchReviewsAnalyticsForAllLocations();
        } else {
          // reset everything if no locations selected
          this.resetDashboardStats();
        }
      } catch (err) {
        console.log("Error updating the settings ", err);
      } finally {
        this.isLoading = false; // Stop loading
        this.$nextTick(() => {
          if (this.selectedWIDs.length > 0 && !this.isLoading) {
            this.renderBarChart();
          }
        });
      }
    },

    // 2) Time Range changed
    async handleTimeRangeChange() {
      console.log("Time range changed to:", this.selectedTimeRange);
      // Re-compute your stats from monthlyBuckets
      this.updateDashboardStats();
      this.$nextTick(() => {
        this.renderBarChart();
      });
    },

    // New method to ensure the chart initializes properly
    initializeDashboard(monthlyBuckets, presentMonthData) {
      this.monthlyBuckets = monthlyBuckets || [];
      this.presentMonthData = presentMonthData || {};

      // Make sure we update with the selected time range
      this.updateDashboardStats();
    },

    // 3) Actually fetch from your Cloud Function
    async fetchReviewsAnalyticsForAllLocations(forceRefresh = false) {
      try {
        this.isLoading = true;

        // Check cache before making API call (unless force refresh)
        if (!forceRefresh) {
          const cacheResult = this.checkCache();

          if (cacheResult) {
            // Use cache data
            console.log("Using cached review dashboard data");
            const { monthlyBuckets, presentMonthData } = cacheResult;

            // Initialize dashboard with cached data
            this.monthlyBuckets = monthlyBuckets;
            this.presentMonthData = presentMonthData;
            this.updateDashboardStats();
            return;
          }
        } else {
          console.log("Force refresh requested, bypassing cache");
          this.clearCache();
        }

        // No cache or expired - make an API call
        // Get workspace details to identify shallow workspaces
        const workspacesDetails = await Promise.all(
          this.selectedWIDs.map(async (wid) => {
            const workspaceRef = doc(
              db,
              "users",
              this.currentUID,
              "fly_workspaces",
              wid
            );
            const workspaceDoc = await getDoc(workspaceRef);
            return {
              wid,
              placeId: workspaceDoc.data()?.placeId,
              isShallow:
                workspaceDoc.exists() &&
                workspaceDoc.data()?.shallowConnect === true,
            };
          })
        );
        // Process all workspaces
        const accounts = [];

        // Process each workspace based on whether it's shallow or not
        for (let i = 0; i < workspacesDetails.length; i++) {
          const { wid, isShallow, placeId } = workspacesDetails[i];

          if (isShallow) {
            // For shallow workspaces, just add the wid without checking connections
            accounts.push({
              uid: this.currentUID,
              wid,
              location: placeId,
              isShallow: true,
            });
          } else {
            // For regular workspaces, check for GMB connection
            const connectedAppsRef = collection(
              db,
              "users",
              this.currentUID,
              "fly_workspaces",
              wid,
              "connected-apps"
            );

            const gbpSnapshot = await getDocs(
              query(
                connectedAppsRef,
                where("__name__", "==", "google_business_profile")
              )
            );

            if (!gbpSnapshot.empty) {
              const gbpData = gbpSnapshot.docs[0].data();
              const accountId = gbpData.accounts?.[0]?.id || "";

              (gbpData.accounts || []).forEach((account) => {
                const locationId = account.locations?.[0]?.name || "";
                accounts.push({
                  uid: this.currentUID,
                  wid,
                  location: locationId,
                  account: accountId,
                  isShallow: false,
                });
              });
            }
          }
        }

        if (accounts.length === 0) {
          return;
        }
        const payload = {
          uid: this.currentUID || this.UID,
          accounts,
          action: "get_gmb_reviews_analytics",
          platform: "google_business_profile",
          sort: this.sortOrder,
        };
        const idToken = await auth.currentUser.getIdToken();
        // check how much time it tokk to get the response
        const startTime = Date.now();
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/getReviewDashboardAnalytics`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
          }
        );
        if (response.ok) {
          const endTime = Date.now();
          const duration = endTime - startTime;
          console.log(`Time taken: ${duration} milliseconds`);
          const result = await response.json(); // <-- THIS IS THE KEY STEP
          console.log("Success! Data from server:", result);
          if (result.success) {
          const { monthlyBuckets, presentMonthData } = result.data || {};

          // Cache the data
          this.cacheData(monthlyBuckets, presentMonthData);

          // Initialize dashboard with API data
          this.initializeDashboard(monthlyBuckets, presentMonthData);
        }
        } else {
          console.error("Request failed with status:", response.status);
        }
        const startTime2 = Date.now();

        const endTime2 = Date.now();
        const duration2 = endTime2 - startTime2;
        console.log(`Time taken: ${duration2} milliseconds`);

      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        this.isLoading = false;
      }
    },

    // 4) Summation & UI calculations
    updateDashboardStats() {
      // Filter monthlyBuckets by the selectedTimeRange
      const filtered = this.getTimeFilteredBuckets();

      // Sum up star ratings, total reviews, text count, reply count, etc.
      let totalReviews = 0;
      let totalStars = 0; // for computing average
      const ratingBreakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      let totalText = 0;
      let totalReplied = 0;

      filtered.forEach((bucket) => {
        // 4.1 total reviews
        totalReviews += bucket.totalReviews || 0;

        // 4.2 ratingCounts => { ONE: 3, TWO: 2, etc. }
        if (bucket.ratingCounts) {
          Object.entries(bucket.ratingCounts).forEach(([key, val]) => {
            const numericStar = this.convertStarStringToNum(key); // 'ONE' => 1, etc.
            ratingBreakdown[numericStar] += val;
          });
        }

        // 4.3 text reviews = sum of commentCount
        totalText += bucket.commentCount || 0;

        // 4.4 replied reviews = sum of reviewReplyCount
        totalReplied += bucket.reviewReplyCount || 0;
      });

      // Summation of star rating
      Object.entries(ratingBreakdown).forEach(([star, count]) => {
        totalStars += Number(star) * count;
      });

      const avgRating = totalReviews > 0 ? totalStars / totalReviews : 0;

      // Set to data
      this.totalReviews = totalReviews;
      this.ratingBreakdown = ratingBreakdown;
      this.averageRating = avgRating;

      // Positive vs Negative can be your own definition:
      // e.g. positive => 4 star or 5 star, negative => 1,2,3
      const positiveCount =
        ratingBreakdown[3] + ratingBreakdown[4] + ratingBreakdown[5];
      const negativeCount = ratingBreakdown[1] + ratingBreakdown[2];

      this.positiveCount = positiveCount;
      this.negativeCount = negativeCount;

      // Text vs No Text
      this.textReviews = totalText;
      const noText = totalReviews - totalText;
      this.noTextReviews = noText;
      this.textPercentage =
        totalReviews > 0 ? Math.round((totalText / totalReviews) * 100) : 0;
      this.noTextPercentage = 100 - this.textPercentage;

      // Replied vs Not Replied
      this.repliedReviews = totalReplied;
      const notReplied = totalReviews - totalReplied;
      this.notRepliedReviews = notReplied;
      this.repliedPercentage =
        totalReviews > 0 ? Math.round((totalReplied / totalReviews) * 100) : 0;
      this.notRepliedPercentage = 100 - this.repliedPercentage;
    },

    // 5) Helper to return a slice of monthlyBuckets for the chosen range
    getTimeFilteredBuckets() {
      if (!this.monthlyBuckets.length) return [];

      if (!this.selectedTimeRange) {
        // "All Time" - sort by date ascending (oldest to newest)
        return [...this.monthlyBuckets].sort((a, b) => {
          return new Date(a.month) - new Date(b.month);
        });
      }

      // Otherwise parse the user's choice
      let monthsCount = 0;
      if (this.selectedTimeRange === "ONE") monthsCount = 1;
      if (this.selectedTimeRange === "TWO") monthsCount = 6;
      if (this.selectedTimeRange === "THREE") monthsCount = 12;

      // Sort buckets by date ascending, then take last X months
      const sorted = [...this.monthlyBuckets].sort((a, b) => {
        return new Date(a.month) - new Date(b.month);
      });

      // Take the last X months (most recent months)
      return sorted.slice(-monthsCount);
    },

    // 6) Render the chart with the filtered monthly data
    renderBarChart() {
      const ctx = this.$refs.barChartCanvas?.getContext("2d");
      if (!ctx) {
        console.error("Chart canvas context not found");
        return;
      }

      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      const filteredBuckets = this.getTimeFilteredBuckets();

      // Check if we have data to display
      if (!filteredBuckets || filteredBuckets.length === 0) {
        console.warn("No data available for chart");
        // Create an empty chart with a message
        this.createEmptyChart(ctx);
        return;
      }

      console.log(
        "Rendering chart with data:",
        filteredBuckets.length,
        "months"
      );

      const totalMonths = filteredBuckets.length;

      // Calculate thumb width as percentage of visible window
      this.thumbWidth = Math.min(100, (this.visibleMonths / totalMonths) * 100);

      // If we're showing last 12 months, set visibleMonths to 12
      if (this.selectedTimeRange === "THREE") {
        this.visibleMonths = Math.min(12, totalMonths);
      }

      // Prepare chart data
      const labels = filteredBuckets.map((b) => {
        const d = new Date(b.month);
        return d.toLocaleString("default", { month: "short", year: "2-digit" });
      });

      const reviewData = filteredBuckets.map((b) => b.totalReviews || 0);
      const avgRatingData = filteredBuckets.map((b) => {
        const ratingCounts = b.ratingCounts || {};
        let sumStars = 0;
        let sumReviews = 0;
        Object.entries(ratingCounts).forEach(([key, val]) => {
          const starNum = this.convertStarStringToNum(key);
          sumStars += starNum * val;
          sumReviews += val;
        });
        return sumReviews ? sumStars / sumReviews : 0;
      });

      const ratingCountsData = filteredBuckets.map((b) => {
        const counts = {
          FIVE: 0,
          FOUR: 0,
          THREE: 0,
          TWO: 0,
          ONE: 0,
          ...b.ratingCounts,
        };
        return counts;
      });

      // Function to determine dot color based on rounded rating
      const getDotColor = (rating) => {
        const roundedRating = Math.round(rating);
        switch (roundedRating) {
          case 5:
            return "#16A34A"; // bg-green-600
          case 4:
            return "#4ADE80"; // bg-green-400
          case 3:
            return "#EAB308"; // bg-yellow-500
          case 2:
            return "#F97316"; // bg-orange-500
          case 1:
            return "#fc164c"; // bg-red-500
          case 0:
            return "#f34409"; // bg-red-500
          default:
            return "#4CAF50"; // fallback color
        }
      };

      // Create arrays for point colors, border colors, and hover colors
      const pointBackgroundColors = avgRatingData.map((rating) =>
        getDotColor(rating)
      );
      const pointBorderColors = avgRatingData.map((rating) =>
        getDotColor(rating)
      );
      const pointHoverBackgroundColors = avgRatingData.map((rating) =>
        getDotColor(rating)
      );

      const maxReviews = Math.max(...reviewData, 5);
      const maxRating = Math.max(...avgRatingData, 5);
      const minRating = Math.min(...avgRatingData, 0);

      // Calculate the range of the dots graph
      const ratingRange = maxRating - minRating || 1; // Avoid division by zero

      this.chartInstance = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Reviews",
              data: reviewData,
              backgroundColor: "#3955fa",
              borderRadius: 4,
              barThickness: 16,
              maxBarThickness: 20,
              yAxisID: "y",
              ratingCounts: ratingCountsData, // Store rating counts for tooltip
            },
            {
              label: "Rating",
              data: avgRatingData,
              type: "line",
              borderColor: "#3955fa",
              backgroundColor: pointBackgroundColors,
              pointBackgroundColor: pointBackgroundColors, // Dynamic dot color
              pointBorderColor: pointBorderColors, // Dynamic border color
              pointHoverBackgroundColor: pointHoverBackgroundColors, // Dynamic hover color
              pointHoverBorderColor: pointHoverBackgroundColors,
              borderWidth: 2,
              pointRadius: 10,
              pointHoverRadius: 12,
              fill: false,
              yAxisID: "y1",
              datalabels: {
                formatter: (value) => value?.toFixed(1),
                color: "white",
                font: {
                  weight: "bold",
                  size: 8,
                },
              },
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: "index",
          },
          layout: {
            padding: {
              top: 30, // Reduced padding
            },
          },
          scales: {
            x: {
              min: Math.max(0, labels.length - this.visibleMonths),
              max: labels.length - 1,
              ticks: {
                autoSkip: false,
                font: { size: 10 },
              },
              grid: {
                display: false, // Remove grid lines for cleaner look
              },
            },
            y: {
              beginAtZero: true,
              max: maxReviews * 1.5, // Reduce max scaling for more compact view
              title: {
                display: false,
              },
              ticks: {
                font: { size: 10 },
                padding: 5,
              },
              grid: {
                borderDash: [2, 2], // Dashed grid lines
                color: "rgba(0, 0, 0, 0.05)",
              },
            },
            y1: {
              position: "right",
              min: 0,
              max: 6, // This will be adjusted dynamically in the plugin
              display: false,
              title: {
                display: false,
              },
              grid: {
                drawOnChartArea: false,
              },
            },
          },
          plugins: {
            tooltip: {
              enabled: true,
              titleFont: { size: 10 },
              bodyFont: { size: 10 },
              callbacks: {
                label: function (context) {
                  const datasetLabel = context.dataset.label;
                  const value = context.parsed.y;

                  if (datasetLabel === "Rating") {
                    return `Avg Rating: ${value?.toFixed(1)}`;
                  }

                  if (datasetLabel === "Reviews") {
                    const ratingCounts =
                      context.dataset.ratingCounts[context.dataIndex];
                    return [
                      `Reviews: ${value}`,
                      `5★: ${ratingCounts.FIVE || 0}`,
                      `4★: ${ratingCounts.FOUR || 0}`,
                      `3★: ${ratingCounts.THREE || 0}`,
                      `2★: ${ratingCounts.TWO || 0}`,
                      `1★: ${ratingCounts.ONE || 0}`,
                    ];
                  }

                  return datasetLabel + ": " + value;
                },
              },
            },
            legend: {
              display: true,
              position: "top",
              labels: {
                boxWidth: 12,
                padding: 10,
                font: { size: 10 },
              },
            },
            zoom: {
              pan: {
                enabled: false,
              },
            },
          },
        },
        plugins: [
          {
            id: "customPointLabels",
            afterDatasetsDraw(chart) {
              const ctx = chart.ctx;
              chart.data.datasets.forEach((dataset, i) => {
                if (dataset.type === "line") {
                  const meta = chart.getDatasetMeta(i);
                  meta.data.forEach((point, index) => {
                    const value = dataset.data[index]?.toFixed(1);
                    ctx.save();
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    ctx.fillStyle = "white";
                    ctx.font = "bold 8px Arial";
                    ctx.fillText(value, point.x, point.y);
                    ctx.restore();
                  });
                }
              });
            },
          },
          {
            id: "shiftDotsGraphAboveBars",
            beforeDatasetsDraw(chart) {
              const barMeta = chart.getDatasetMeta(0); // Bar dataset (Reviews)
              const dotsMeta = chart.getDatasetMeta(1); // Dots dataset (Rating)

              if (barMeta && dotsMeta && barMeta.data && dotsMeta.data) {
                // Find the tallest bar height
                const tallestBarValue = Math.max(
                  ...chart.data.datasets[0].data
                );
                const tallestBarTopPixel =
                  chart.scales.y.getPixelForValue(tallestBarValue);

                // Get the y1 scale (Rating)
                const y1Scale = chart.scales.y1;

                // Get min and max rating values
                const minRating = Math.min(...chart.data.datasets[1].data);
                const maxRating = Math.max(...chart.data.datasets[1].data);
                const ratingRange = maxRating - minRating || 1; // Avoid division by zero

                // Calculate pixel range for dots (smaller proportion of chart height)
                const chartHeight =
                  chart.chartArea.bottom - chart.chartArea.top;
                const dotsGraphHeight = chartHeight * 0.25; // Allocate 25% of chart height for dots
                const pixelPerRatingUnit = dotsGraphHeight / ratingRange;

                // Position dots above bars with less padding
                const paddingAboveBar = 15; // Reduced padding
                const dotsGraphBasePixel =
                  tallestBarTopPixel - paddingAboveBar - dotsGraphHeight;

                // Update y1 scale
                y1Scale.options.min = minRating;
                y1Scale.options.max = maxRating;

                // Position each dot
                dotsMeta.data.forEach((point, index) => {
                  const ratingValue = chart.data.datasets[1].data[index];
                  const relativeRating = ratingValue - minRating;
                  const pixelOffset = relativeRating * pixelPerRatingUnit;
                  point.y =
                    dotsGraphBasePixel + (dotsGraphHeight - pixelOffset);

                  // Keep dots visible
                  const minY = chart.chartArea.top + 10; // Reduced minimum
                  if (point.y < minY) {
                    point.y = minY;
                  }
                });
              }
            },
          },
        ],
      });

      // Initialize scroll position to show most recent months
      this.updateScrollPosition(100 - this.thumbWidth);
    },

    // New methods for scrolling functionality
    scrollChart(direction) {
      const scrollAmount = 10; // Percentage to scroll
      let newPosition;

      if (direction === "left") {
        newPosition = Math.max(0, this.thumbPosition - scrollAmount);
      } else {
        newPosition = Math.min(
          100 - this.thumbWidth,
          this.thumbPosition + scrollAmount
        );
      }

      this.updateScrollPosition(newPosition);
    },

    updateScrollPosition(position) {
      this.thumbPosition = position;

      if (this.chartInstance) {
        const totalMonths = this.chartInstance.data.labels.length;
        const startIndex = Math.floor(
          (position / (100 - this.thumbWidth)) *
            (totalMonths - this.visibleMonths)
        );

        this.chartInstance.options.scales.x.min = Math.max(0, startIndex);
        this.chartInstance.options.scales.x.max = Math.min(
          totalMonths - 1,
          startIndex + this.visibleMonths - 1
        );
        this.chartInstance.update();
      }
    },

    startDrag(event) {
      this.isDragging = true;
      const startX =
        event.type === "mousedown" ? event.clientX : event.touches[0].clientX;
      this.startX = startX;
      this.scrollStartPosition = this.thumbPosition;

      // Add event listeners for drag movement and release
      if (event.type === "mousedown") {
        document.addEventListener("mousemove", this.doDrag);
        document.addEventListener("mouseup", this.stopDrag);
      } else {
        document.addEventListener("touchmove", this.doDrag);
        document.addEventListener("touchend", this.stopDrag);
      }

      // Prevent default to avoid text selection
      event.preventDefault();
    },

    doDrag(event) {
      if (!this.isDragging) return;

      const currentX =
        event.type === "mousemove" ? event.clientX : event.touches[0].clientX;
      const diffX = currentX - this.startX;
      const trackWidth = this.$refs.scrollTrack.offsetWidth;

      // Calculate percentage moved
      const percentageMoved = (diffX / trackWidth) * 100;

      // Calculate new position
      let newPosition = this.scrollStartPosition + percentageMoved;

      // Constrain between 0 and (100 - thumbWidth)
      newPosition = Math.max(0, Math.min(100 - this.thumbWidth, newPosition));

      this.updateScrollPosition(newPosition);
    },

    stopDrag() {
      this.isDragging = false;

      // Remove event listeners
      document.removeEventListener("mousemove", this.doDrag);
      document.removeEventListener("mouseup", this.stopDrag);
      document.removeEventListener("touchmove", this.doDrag);
      document.removeEventListener("touchend", this.stopDrag);
    },

    // Utils
    convertStarStringToNum(str) {
      switch (str) {
        case "ONE":
          return 1;
        case "TWO":
          return 2;
        case "THREE":
          return 3;
        case "FOUR":
          return 4;
        case "FIVE":
          return 5;
        default:
          return 0;
      }
    },
    getStarPercentage(star) {
      // star is 1..5
      const count = this.ratingBreakdown[star];
      if (!this.totalReviews) return 0;
      return Math.round((count / this.totalReviews) * 100);
    },
    resetDashboardStats() {
      // optional helper if user deselects all WIDs
      this.monthlyBuckets = [];
      this.presentMonthData = {};
      this.totalReviews = 0;
      this.averageRating = 0;
      this.ratingBreakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      this.positiveCount = 0;
      this.negativeCount = 0;
      this.textReviews = 0;
      this.noTextReviews = 0;
      this.textPercentage = 0;
      this.noTextPercentage = 0;
      this.repliedReviews = 0;
      this.notRepliedReviews = 0;
      this.repliedPercentage = 0;
      this.notRepliedPercentage = 0;
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
    },
    modifyUrlParams(param, value) {
      // Check if the current value in the URL is already the same
      if (this.$route.query[param] === value) {
        return; // Skip redundant navigation
      }
      // Only update the URL if the value is different
      this.$router.replace({ query: { ...this.$route.query, [param]: value } });
    },
    async fetchStores() {
      // Use the global stores from Vuex
      this.stores = [];
      this.selectedWIDs = [];
      const locationParam = this.$route.query.location;
      const locationIds = locationParam
        ? Array.isArray(locationParam)
          ? locationParam
          : [locationParam]
        : [];
      // Convert to array if it's a comma-separated string (e.g., "id1,id2,id3")
      const locationIdsArray =
        locationIds.length === 1 && locationIds[0].includes(",")
          ? locationIds[0].split(",").map((id) => id.trim())
          : locationIds;
      // Process the existing store data
      this.globalStores.forEach((storeData) => {
        // Add computed properties that aren't stored in Firestore
        const shouldBeChecked =
          locationIdsArray.length === 0 ||
          locationIdsArray.includes(storeData.id);
        this.stores.push({
          ...storeData,
          id: storeData.id,
          wid: storeData.id,
          type: "store",
          // checked: locationParam ? shouldBeChecked : true,
          checked: shouldBeChecked,
          isReview: true,
          isSelectable: true,
          tooltipMessage: ""
        });
        if (shouldBeChecked) {
          this.selectedWIDs.push(storeData.id);
        }
      });

      if (this.selectedWIDs.length > 0) {
        this.isLoading = true; // Set isLoading before async operation
        try {
          // Use cached data if available instead of forcing refresh on initial load
          await this.fetchReviewsAnalyticsForAllLocations(false);
        } catch (error) {
          console.error("Error fetching analytics in fetchStores:", error);
        } finally {
          this.isLoading = false; // Clear isLoading in finally
          this.$nextTick(() => {
            // Ensure DOM is updated after isLoading changed to false
            if (this.selectedWIDs.length > 0 && !this.isLoading) {
              this.renderBarChart();
            }
          });
        }
      } else {
        this.isLoading = false; // Ensure isLoading is false if no locations
        this.resetDashboardStats(); // Clear stats and destroy chart
      }
    },
    toggleStoreDropdown(event) {
      event.stopPropagation();
      this.showStoreDropdown = !this.showStoreDropdown;
    },
    handleClickOutside(event) {
      const dropdown = this.$refs.locationDropdown?.$refs.showStoreDropdown;
      const input = event.target.closest(".location-dropdown");
      if (!input && dropdown && !dropdown.contains(event.target)) {
        this.showStoreDropdown = false;
      }
    },
    // Screenshot capture method
    async captureScreenshot() {
      try {
        // Show a loading indicator
        this.$store.commit("showSnackbar", {
          text: "Preparing screenshot...",
          color: "info",
        });

        // Get the dashboard element to capture
        const dashboardElement = document.querySelector(
          ".bg-white.rounded-lg.shadow-md"
        );
        if (!dashboardElement) {
          throw new Error("Dashboard element not found");
        }

        // Use html2canvas to capture the element
        const canvas = await html2canvas(dashboardElement, {
          scale: 2, // Higher quality
          useCORS: true, // Allow cross-origin images
          logging: false,
          backgroundColor: "#ffffff",
        });

        // Convert to image and download
        const imageData = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = imageData;

        // Generate filename with current date
        const date = new Date();
        const formattedDate = date.toISOString().split("T")[0];
        link.download = `reviews-${formattedDate}.png`;

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Success message
        this.$snackbar.show("Screenshot saved");
      } catch (error) {
        console.error("Error capturing screenshot:", error);
        this.$snackbar.show("Failed to save screenshot");
      }
    },
    handleConnectGMB() {
      try {
        this.$snackbar.show("Please wait a moment");
        this.isLoading = true;

        // Use the initConnection method from the connected.apps.mixins.js
        this.initConnection("google_business_profile", "bulk");

        setTimeout(() => {
          this.$snackbar.hide();
        }, 2000);
      } catch (error) {
        console.error("Error connecting Google Business Profile:", error);
        this.$alert.show(
          "Failed to start Google Business connection. Please try again."
        );
      } finally {
        this.isLoading = false;
      }
    },
    // New methods for caching
    checkCache() {
      try {
        // Generate a unique cache key based on user ID and selected locations
        const cacheKey = this.getCacheKey();
        const cachedData = localStorage.getItem(cacheKey);

        // console.log("Checking cache with key:", cacheKey);

        if (!cachedData) {
          console.log("No cache found");
          return null;
        }

        const parsedCache = JSON.parse(cachedData);
        const { timestamp, monthlyBuckets, presentMonthData } = parsedCache;

        // Check if cache is expired (older than cacheDuration)
        const now = Date.now();
        if (now - timestamp > this.cacheDuration) {
          console.log("Cache expired, fetching fresh data");
          localStorage.removeItem(cacheKey);
          return null;
        }

        // Validate that we have actual data
        if (
          !monthlyBuckets ||
          !Array.isArray(monthlyBuckets) ||
          monthlyBuckets.length === 0
        ) {
          console.log("Cache invalid - empty monthly buckets");
          localStorage.removeItem(cacheKey);
          return null;
        }
        console.log("Valid cache found, using cached data");
        return { monthlyBuckets, presentMonthData };
      } catch (error) {
        console.error("Error checking cache:", error);
        // Clear invalid cache
        try {
          localStorage.removeItem(this.getCacheKey());
        } catch (e) {
          console.error("Failed to remove invalid cache", e);
        }
        return null;
      }
    },

    cacheData(monthlyBuckets, presentMonthData) {
      try {
        // Don't cache empty data
        if (
          !monthlyBuckets ||
          !Array.isArray(monthlyBuckets) ||
          monthlyBuckets.length === 0
        ) {
          console.log("Not caching empty data");
          return;
        }

        const cacheKey = this.getCacheKey();
        const cacheData = {
          timestamp: Date.now(),
          monthlyBuckets,
          presentMonthData,
        };

        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        console.log("Dashboard data cached successfully with key:", cacheKey);
      } catch (error) {
        console.error("Error caching data:", error);
      }
    },

    getCacheKey() {
      // Create a unique key based on user ID and selected WIDs
      const sortedWIDs = [...this.selectedWIDs].sort().join("_");
      return `reviewDashboardCache_${this.currentUID}_${sortedWIDs}`;
    },

    clearCache() {
      try {
        const cacheKey = this.getCacheKey();
        localStorage.removeItem(cacheKey);
        console.log("Cache cleared for key:", cacheKey);
      } catch (error) {
        console.error("Error clearing cache:", error);
      }
    },

    // Manually refresh data
    async refreshData() {
      if (this.selectedWIDs.length === 0) {
        this.$snackbar.show("No locations selected");
        return;
      }

      try {
        this.$snackbar.show("Refreshing data...");
        // fetchReviewsAnalyticsForAllLocations handles its own isLoading state.
        // It will be true during the call and false after it completes (in its finally block).
        await this.fetchReviewsAnalyticsForAllLocations(true);
        this.$nextTick(() => {
          // We check this.isLoading here to ensure the component's overall loading state allows rendering.
          // After await, fetchReviewsAnalyticsForAllLocations has set its isLoading to false.
          if (this.selectedWIDs.length > 0 && !this.isLoading) {
            this.renderBarChart();
          }
        });
        this.$snackbar.show("Data refreshed successfully");
      } catch (error) {
        console.error("Error refreshing data:", error);
        this.$snackbar.show("Failed to refresh data");
      }
    },

    // Create an empty chart with a message
    createEmptyChart(ctx) {
      this.chartInstance = new Chart(ctx, {
        type: "bar",
        data: {
          labels: ["No Data"],
          datasets: [
            {
              label: "Reviews",
              data: [0],
              backgroundColor: "#e5e7eb",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              max: 5,
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
            },
          },
        },
        plugins: [
          {
            id: "noDataLabel",
            afterDraw: (chart) => {
              const {
                ctx,
                chartArea: { left, right, top, bottom, width, height },
              } = chart;

              ctx.save();
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.font = "16px sans-serif";
              ctx.fillStyle = "#6b7280";
              ctx.fillText(
                "No review data available for the selected time period",
                width / 2 + left,
                height / 2 + top
              );
              ctx.restore();
            },
          },
        ],
      });
    },
  },
};
</script>

<style scoped>
.chart-scroller {
  width: 100%;
}

.scroll-btn-sm {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s;
}

.scroll-btn-sm:hover {
  background-color: #e5e7eb;
}

.scroll-track {
  position: relative;
  cursor: pointer;
}

.scroll-thumb {
  position: absolute;
  top: 0;
  background-color: #3955fa;
  cursor: grab;
  border-radius: 4px;
}

.scroll-thumb:active {
  cursor: grabbing;
}

.custom-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 50;
}

/* Make text in chart clearer */
canvas text {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
}
</style>
